import { useEffect, useRef, useState } from 'react';
import { WithFeatureService } from 'v5/services/front/FeatureService/FeatureService';
import { useForm } from 'react-hook-form';
import { Button } from '../../design-system/Button';
import { Form } from '../../design-system/Form';
import { FormDialogContent } from 'v5/patterns/forms/dialogs/FormDialogContent';
import { DialogErrorFooter } from 'v5/patterns/forms/dialogs/DialogErrorFooter';
import { useRegisterDirtyState } from 'v5/patterns/forms/unsaved-changes/useRegisterDirtyState';
import {
  getLocalTimeZone,
  isSameDay,
  Time,
  today
} from '@internationalized/date';
import { ProductionSelectionStep } from './steps/ProductionSelectionStep';
import { CallsheetDetailsStep } from './steps/CallsheetDetailsStep';
import type { CallsheetFormData } from './types';
import { promiseState } from '@passionware/platform-react';
import { generateCallsheetTitleFromProduction } from 'v1/helpers/byModel/CallsheetHelper';
import { AsyncButton } from 'v5/patterns/forms/fields/AsyncButton';
import { productionLogic } from 'v5/api/callsheet/callsheet.logic';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import { WithProductionService } from 'v5/services/io/ProductionService/ProductionService';
import { WithServices } from 'v5/platform/ts/services';
import { cn } from 'v5/platform/dom/cn';
import { DialogBody } from 'v5/design-system/Dialog';
import { useResponsiveDialogBody } from 'v5/design-system/useResponsiveDialogBody';
import { WithProductionTypeService } from 'v5/services/internal/ProductionTypeService/ProductionTypeService';
import { WithMetastructureMappingService } from 'v5/services/internal/MetastructureMappingService/MetastructureMappingService';
import { WithResourceTypeService } from 'v5/services/internal/ResourceTypeService/ResourceTypeService';
import { WithResourceService } from 'v5/services/io/ResourceService/ResourceService';
import { WithTagService } from 'v5/services/io/TagsService/TagsService';
import { WithGroupsService } from 'v5/services/io/GroupsService/GroupsService';
import { WithUserPreferencesService } from 'v5/services/front/UserPreferencesService/UserPreferencesService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { rd } from '@passionware/monads';
import {
  workspaceIdFromScope,
  workspaceScopingQueryUtils
} from 'v5/api/_common/query/workspace-scoping';
import {
  teamIdFromScope,
  teamScopingQueryUtils
} from 'v5/api/_common/query/team-scoping';
import { ScopeBadge } from 'v5/patterns/page-layout/ScopeBadge';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';

const defaultFormValues: CallsheetFormData = {
  selectedProductionIds: [],
  title: '',
  dateSelection: { type: 'single', date: today(getLocalTimeZone()) },
  generalCallTime: new Time(9, 0)
};

export interface CreateCallsheetDialogContentProps
  extends WithServices<
    [
      WithProductionService,
      WithProductionTypeService,
      WithMetastructureMappingService,
      WithResourceTypeService,
      WithResourceService,
      WithTagService,
      WithGroupsService,
      WithUserPreferencesService,
      WithAppScopeService,
      WithEnterpriseService,
      WithFeatureService
    ]
  > {
  onSubmit?: (data: CallsheetFormData) => Promise<void> | void;
  open?: boolean;
  preselectedProductionIds?: number[];
}

type Step = 'production-selection' | 'details';

/**
 * Create Callsheet Dialog Content - v5 implementation
 *
 * Multi-step callsheet creation flow:
 * 1. Production selection - Select one or more productions
 * 2. Callsheet details - Enter title, date, general call time, and review selected productions
 */
export function CreateCallsheetDialogContent({
  onSubmit,
  open = true,
  preselectedProductionIds,
  services
}: CreateCallsheetDialogContentProps) {
  const [currentStep, setCurrentStep] = useState<Step>(
    preselectedProductionIds && preselectedProductionIds.length > 0
      ? 'details'
      : 'production-selection'
  );
  const scope = rd.tryGet(services.appScopeService.useResolvedAppScope());
  const mutation = promiseState.useMutation(async (data: CallsheetFormData) => {
    return onSubmit?.({
      ...data,
      enterprise_workspace_id: workspaceIdFromScope(scope),
      enterprise_team_id: teamIdFromScope(scope)
    });
  });

  const form = useForm<CallsheetFormData>({
    defaultValues: {
      ...defaultFormValues,
      selectedProductionIds: preselectedProductionIds ?? []
    }
  });

  const selectedProductionIds = form.watch('selectedProductionIds');
  const hasSetSmartDateRef = useRef(false);
  const lastAutoGeneratedTitleRef = useRef<string>('');

  // Responsive scrolling for dialog body
  const responsive = useResponsiveDialogBody(
    currentStep === 'details' ? 650 : 550
  );

  // Register dirty state with UnsavedChangesProtection context
  useRegisterDirtyState(form.formState.isDirty);

  // Reset smart date ref when production selection changes
  useEffect(() => {
    hasSetSmartDateRef.current = false;
  }, [selectedProductionIds]);

  // Generate title and smart default date when productions are selected
  useEffect(() => {
    if (currentStep !== 'details' || selectedProductionIds.length === 0) {
      return;
    }

    let cancelled = false;

    const generateDefaults = async () => {
      const productions = await Promise.all(
        selectedProductionIds.map(id =>
          services.productionService.ensureProduction(id)
        )
      );

      if (cancelled) return;

      // Generate title on first fill and keep it in sync when it was auto-generated.
      // If user edited title manually, do not overwrite.
      const titles = productions
        .map(production => generateCallsheetTitleFromProduction(production))
        .filter(Boolean);
      if (titles.length) {
        const generatedTitle = titles.join(', ');
        const currentTitle = form.getValues('title');
        const isEmpty = !currentTitle;
        const isPreviousAutoTitle =
          currentTitle === lastAutoGeneratedTitleRef.current;

        if (isEmpty || isPreviousAutoTitle) {
          form.setValue('title', generatedTitle, { shouldDirty: false });
          lastAutoGeneratedTitleRef.current = generatedTitle;
        }
      }

      // Generate smart default date if we haven't set it yet or if it's still the default
      // We only set it once per production selection to avoid overwriting user changes
      if (!hasSetSmartDateRef.current) {
        const currentSelection = form.getValues('dateSelection');
        const defaultToday = today(getLocalTimeZone());
        const isStillDefaultDate =
          currentSelection.type === 'single' &&
          isSameDay(currentSelection.date, defaultToday);

        if (isStillDefaultDate) {
          const smartDate =
            productionLogic.calculateSmartDefaultRange(productions);
          if (smartDate) {
            form.setValue(
              'dateSelection',
              { type: 'single', date: smartDate },
              { shouldDirty: false }
            );
            hasSetSmartDateRef.current = true;
          }
        }
      }
    };

    generateDefaults();

    return () => {
      cancelled = true;
    };
  }, [currentStep, form, selectedProductionIds, services.productionService]);

  const handleCreateFromScratch = () => {
    // Skip production selection and go directly to details step
    form.setValue('selectedProductionIds', [], { shouldDirty: false });
    setCurrentStep('details');
  };

  const handleSubmit = (data: CallsheetFormData) => {
    // Handle step navigation
    if (currentStep === 'production-selection') {
      if (data.selectedProductionIds.length > 0) {
        setCurrentStep('details');
      }
      return;
    }

    // Final submission from details step
    if (currentStep === 'details') {
      mutation.track(data);
    }
  };

  const handleBack = () => {
    if (currentStep === 'details') {
      setCurrentStep('production-selection');
    }
  };

  const getStepDescription = (): string => {
    switch (currentStep) {
      case 'production-selection':
        return 'Select one or more productions to auto-populate callsheets. Use arrow keys to navigate, Enter or Space to toggle selection.';
      case 'details':
        return 'Enter the callsheet details including title, date, and general call time. Resources from selected productions will be added to the callsheet.';
      default:
        return '';
    }
  };

  const getSaveButtonText = (): string => {
    switch (currentStep) {
      case 'production-selection':
        return 'Next';
      case 'details':
        return 'Create callsheet';
      default:
        return 'Next';
    }
  };

  const canProceed = (): boolean => {
    if (currentStep === 'production-selection') {
      return selectedProductionIds.length > 0;
    }
    if (currentStep === 'details') {
      const title = form.watch('title');
      const dateSelection = form.watch('dateSelection');

      if (!title) return false;

      if (dateSelection.type === 'range') {
        return !!dateSelection.from && !!dateSelection.to;
      }
      return !!dateSelection.date;
    }
    return true;
  };

  return (
    <Form {...form}>
      <FormDialogContent
        onSubmit={form.handleSubmit(handleSubmit)}
        open={open}
        title={
          <>
            New callsheet
            <ScopeBadge
              scope={
                scope && {
                  ...workspaceScopingQueryUtils.fromScope(scope),
                  ...teamScopingQueryUtils.fromScope(scope)
                }
              }
              meaning="createsIn"
              services={services}
            />
          </>
        }
        saveText={getSaveButtonText()}
        mutation={mutation.state}
        description={getStepDescription()}
        className={cn(
          'max-w-4xl',
          'h-[70vh]',
          'min-h-[calc(min(700px,100vh-2rem))]'
        )}
        style={responsive.containerStyle}
        renderDialogBody={children => (
          <DialogBody
            className={cn('space-y-6', responsive.bodyClassName)}
            scrollerClassName={responsive.scrollerClassName}
          >
            {children}
          </DialogBody>
        )}
        footerContent={
          <>
            {currentStep === 'production-selection' && (
              <SimpleTooltip title="To create a callsheet from scratch, unselect all productions.">
                <Button
                  type="button"
                  variant="ghost"
                  visuallyDisabled={selectedProductionIds.length > 0}
                  onClick={handleCreateFromScratch}
                  className="mr-auto"
                >
                  Create from Scratch
                </Button>
              </SimpleTooltip>
            )}
            {currentStep !== 'production-selection' && (
              <Button
                type="button"
                variant="ghost"
                onClick={handleBack}
                className="mr-auto"
              >
                ← Back
              </Button>
            )}
            <DialogErrorFooter mutation={mutation.state} />
          </>
        }
        saveButtonSlot={
          <AsyncButton
            type="submit"
            mutation={mutation.state}
            variant="primary"
            visuallyDisabled={!canProceed()}
          >
            {getSaveButtonText()}
          </AsyncButton>
        }
      >
        {currentStep === 'production-selection' && (
          <ProductionSelectionStep form={form} services={services} />
        )}
        {currentStep === 'details' && <CallsheetDetailsStep form={form} />}
      </FormDialogContent>
    </Form>
  );
}
